import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import ClassesCardHList from "../../components/ClassesCardHList";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../redux/states";
import { BaseState, StateKeys } from "../../redux/states/main.state";
import Main from "../../redux/actions/main.action";
import ApiEndpoint from "../../services/api.endpoint.service";
import { RouteEndpoints } from "../../services/route.service";
import {
  AppState,
  CheckContentProviderIdExist,
} from "../../services/app.service";
import {
  CategoryResponse,
  ClassResponse,
  ContentResponse,
  InterestResponse,
  Links,
} from "../../services/api.response.service";
import CourseRecentList from "../../components/CourseRecentList";
import Languages from "../../components/Languages";
import CategoryList from "../../components/CategoryList";
import ExploreBundlesBanner from "../../components/ExploreBundlesBanner";
import InterestsModal from "../../components/InterestsModal";
import SignInModal from "../../components/UnAuthorizedPage/SignInModal";
import { Mixpanel } from "../../services/mixpanel";
import Auth from "../../redux/actions/auth.action";
import Api from "../../services/api.service";
import { signOut } from "firebase/auth";
import { auth } from "../../services/firebase.service";

const sliceLength = 10;

/** Guest-only rows below Categories. Bundles use CLASS_BUNDLES; others use HOME_PAGE_CATEGORY_CLASSES. */
const GUEST_HOME_SECTION_CONFIG: ReadonlyArray<{
  stateKey: string;
  titleLangKey: string;
  classBundles?: boolean;
}> = [
  {
    stateKey: StateKeys.CLASS_BUNDLES,
    titleLangKey: "bundles",
    classBundles: true,
  },
  { stateKey: StateKeys["TEST-PREP"], titleLangKey: "testPrep" },
  { stateKey: StateKeys.LIFESTYLE, titleLangKey: "lifestyle" },
  { stateKey: StateKeys.BOOK, titleLangKey: "book" },
];

function shuffleArray<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type InterestSectionState = {
  interest: InterestResponse;
  data: ClassResponse[];
  pageState: AppState;
  hasMore: boolean;
};

type InterestClassesApiBody = {
  data: ClassResponse[];
  links?: Links;
};

/** Keeps one section per selected interest; preserves prior rows when the selection overlaps. */
function mergeSectionsForInterestSelection(
  selectedInterests: InterestResponse[],
  prev: Record<number, InterestSectionState>,
): Record<number, InterestSectionState> {
  const next: Record<number, InterestSectionState> = {};
  for (const interest of selectedInterests) {
    next[interest.id] = prev[interest.id] ?? {
      interest,
      data: [],
      pageState: AppState.LOADING,
      hasMore: false,
    };
  }
  return next;
}

async function fetchInterestClasses(interestId: number) {
  const response = await Api.getInterestsClasses(interestId);
  const { data: classes, links } = response.data as InterestClassesApiBody;
  return { classes, hasMore: !!links?.next };
}

/** Loads classes for one interest and writes LOADING → LOADED or ERROR, unless a newer fetch superseded this run. */
async function fetchAndStoreInterestSection(
  interest: InterestResponse,
  fetchSeq: number,
  seqRef: React.MutableRefObject<number>,
  setInterestSections: React.Dispatch<
    React.SetStateAction<Record<number, InterestSectionState>>
  >,
): Promise<void> {
  setInterestSections((prev) => ({
    ...prev,
    [interest.id]: {
      ...(prev[interest.id] ?? {
        interest,
        data: [],
        hasMore: false,
      }),
      interest,
      pageState: AppState.LOADING,
    },
  }));

  try {
    const { classes, hasMore } = await fetchInterestClasses(interest.id);
    if (seqRef.current !== fetchSeq) return;
    setInterestSections((prev) => ({
      ...prev,
      [interest.id]: {
        interest,
        data: classes,
        pageState: AppState.LOADED,
        hasMore,
      },
    }));
  } catch {
    if (seqRef.current !== fetchSeq) return;
    setInterestSections((prev) => ({
      ...prev,
      [interest.id]: {
        interest,
        data: [],
        pageState: AppState.ERROR,
        hasMore: false,
      },
    }));
  }
}

function HomePage() {
  const stateAll = useSelector((state: IRootState) => state);
  const currentUser = stateAll.setting.user!;
  const isAuthenticated = !!currentUser.ecampus_id;

  const dispatch = useDispatch();
  const state = useSelector((state: IRootState) => state.main);

  const recent: BaseState<Array<ContentResponse>> = state[StateKeys.RECENT];
  const categories: BaseState<Array<CategoryResponse>> =
    state[StateKeys.CATEGORIES];
  const interests: BaseState<Array<InterestResponse>> =
    state[StateKeys.INTERESTS];
  const userInterests: BaseState<Array<InterestResponse>> =
    state[StateKeys.USER_INTERESTS];

  const [interestSections, setInterestSections] = useState<
    Record<number, InterestSectionState>
  >({});
  const [showInterestsGate, setShowInterestsGate] = useState(false);
  const interestFetchSeqRef = useRef(0);
  const lastFetchedInterestKeyRef = useRef<string>("");
  const [orderedUserInterests, setOrderedUserInterests] = useState<
    InterestResponse[]
  >([]);

  const [mp, setMp] = useState<boolean>(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  useEffect(() => {
    if (!CheckContentProviderIdExist() && !mp) {
      Mixpanel.track("Home Page View", {
        with: currentUser?.id ? "Auth" : "Guest",
        user_id: currentUser?.id,
      });
      setMp(true);
    }
  }, [mp, currentUser?.id]);

  useEffect(() => {
    if (recent.pageState !== AppState.LOADED && isAuthenticated) {
      dispatch(
        Main.getPageArrayData({
          stateKey: StateKeys.RECENT,
          apiEndpoint: ApiEndpoint.USER_RECENT_TOPICS,
        }),
      );
    }

    if (categories.pageState !== AppState.LOADED) {
      dispatch(
        Main.getPageArrayData({
          stateKey: StateKeys.CATEGORIES,
          apiEndpoint: ApiEndpoint.ALL_CATEGORIES,
        }),
      );
    }

    if (
      isAuthenticated &&
      userInterests.pageState !== AppState.LOADED &&
      userInterests.pageState !== AppState.LOADING
    ) {
      dispatch(
        Main.getPaginatedPageData({
          stateKey: StateKeys.USER_INTERESTS,
          apiEndpoint: ApiEndpoint.USER_INTERESTS,
          previousPage: userInterests.currentPage!,
        }),
      );
    }

    if (
      isAuthenticated &&
      interests.pageState !== AppState.LOADED &&
      interests.pageState !== AppState.LOADING
    ) {
      dispatch(
        Main.getPaginatedPageData({
          stateKey: StateKeys.INTERESTS,
          apiEndpoint: ApiEndpoint.ALL_INTERESTS,
          previousPage: interests.currentPage!,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) return;
    GUEST_HOME_SECTION_CONFIG.forEach(({ stateKey }) => {
      const pageData = state[stateKey];
      if (
        pageData.pageState !== AppState.LOADED &&
        pageData.pageState !== AppState.LOADING
      ) {
        const apiEndpoint =
          stateKey === StateKeys.CLASS_BUNDLES
            ? ApiEndpoint.CLASS_BUNDLES
            : ApiEndpoint.HOME_PAGE_CATEGORY_CLASSES(stateKey);
        dispatch(
          Main.getPageArrayData({
            stateKey,
            apiEndpoint,
          }),
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const selectedInterestKey = useMemo(
    () =>
      (userInterests.data || [])
        .map((i) => i.id)
        .sort((a, b) => a - b)
        .join(","),
    [userInterests.data],
  );

  useEffect(() => {
    if (!isAuthenticated || userInterests.pageState !== AppState.LOADED) {
      setOrderedUserInterests([]);
      return;
    }

    const list = userInterests.data || [];
    if (list.length === 0) {
      setOrderedUserInterests([]);
      return;
    }

    setOrderedUserInterests(shuffleArray(list));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userInterests.pageState, selectedInterestKey]);

  useEffect(() => {
    if (!isAuthenticated || userInterests.pageState !== AppState.LOADED) {
      return;
    }

    const selectedInterests = userInterests.data || [];

    // Same string as selectedInterestKey — skip if we already fetched for this selection.
    if (lastFetchedInterestKeyRef.current === selectedInterestKey) {
      return;
    }
    lastFetchedInterestKeyRef.current = selectedInterestKey;
    const fetchSeq = ++interestFetchSeqRef.current;

    setInterestSections((prev) =>
      mergeSectionsForInterestSelection(selectedInterests, prev),
    );

    void Promise.all(
      selectedInterests.map((interest) =>
        fetchAndStoreInterestSection(
          interest,
          fetchSeq,
          interestFetchSeqRef,
          setInterestSections,
        ),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userInterests.pageState, selectedInterestKey]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowInterestsGate(false);
      lastFetchedInterestKeyRef.current = "";
      return;
    }

    if (userInterests.pageState !== AppState.LOADED) return;
    setShowInterestsGate(userInterests.data.length === 0);
  }, [isAuthenticated, userInterests.pageState, userInterests.data.length]);

  const handleInterestsSubmitSuccess = () => {
    setShowInterestsGate(false);
    // Force a fresh per-interest section fetch after successful selection update.
    lastFetchedInterestKeyRef.current = "";
    dispatch(
      Main.getPaginatedPageData({
        stateKey: StateKeys.USER_INTERESTS,
        apiEndpoint: ApiEndpoint.USER_INTERESTS,
        previousPage: 0,
        clear: true,
      }),
    );
  };

  function goSomeWhere() {
    if (!currentUser.ecampus_id) {
      signOut(auth);
      dispatch(Auth.logout());
    }

    window.location.href = currentUser.ecampus_id
      ? RouteEndpoints.CONTENT_PROVIDER_CLASSES
      : RouteEndpoints.SIGN_IN;
  }

  return !CheckContentProviderIdExist() ? (
    <>
      <div className="content-page scroller-hide">
        <Row className="d-flex flex-column gap-4">
          <h1 style={{ fontSize: "24px", fontWeight: 400 }}>
            <Languages langKey="startLearning" />!
          </h1>

          <ExploreBundlesBanner />

          {isAuthenticated && (
            <div className="content-card">
              {(recent.pageState === AppState.LOADED ||
                recent.pageState === AppState.LOADING) && (
                <CourseRecentList
                  data={recent.data}
                  state={recent.pageState}
                  viewAllLink={RouteEndpoints.HOME_VIEW_MORE(StateKeys.RECENT)}
                />
              )}
            </div>
          )}

          <div className="category-card">
            {((categories.data.length > 0 &&
              categories.pageState === AppState.LOADED) ||
              categories.pageState === AppState.LOADING) && (
              <CategoryList
                title={"categories"}
                data={categories.data}
                state={categories.pageState}
                viewAllLink={RouteEndpoints.HOME_VIEW_MORE(
                  StateKeys.CATEGORIES,
                )}
              />
            )}
          </div>

          {!isAuthenticated &&
            GUEST_HOME_SECTION_CONFIG.map(
              ({ stateKey, titleLangKey, classBundles }) => {
                const pageData = state[stateKey] as BaseState<ClassResponse[]>;
                return (
                  <div className="content-card" key={`guest-home-${stateKey}`}>
                    <ClassesCardHList
                      title={titleLangKey}
                      data={pageData.data}
                      state={pageData.pageState}
                      viewAllLink={RouteEndpoints.HOME_VIEW_MORE(stateKey)}
                      showUnAuthModal={() => setShowSignInModal(true)}
                      classBundles={classBundles ?? false}
                    />
                  </div>
                );
              },
            )}

          {isAuthenticated &&
            orderedUserInterests.map((interest) => {
              const section = interestSections[interest.id];
              const sectionState = section?.pageState ?? AppState.LOADING;
              const sectionData = section?.data ?? [];
              const viewAllLink = section?.hasMore
                ? RouteEndpoints.INTEREST_CLASSES(`${interest.id}`)
                : undefined;

              if (
                sectionState !== AppState.LOADING &&
                sectionState !== AppState.LOADED
              ) {
                return null;
              }

              if (
                sectionState === AppState.LOADED &&
                sectionData.length === 0
              ) {
                return null;
              }

              return (
                <div
                  className="content-card"
                  key={`interest-section-${interest.id}`}
                >
                  <ClassesCardHList
                    fullTitle={interest.name}
                    data={[...sectionData].slice(0, sliceLength)}
                    state={sectionState}
                    viewAllLink={viewAllLink}
                  />
                </div>
              );
            })}
        </Row>
      </div>
      <InterestsModal
        interests={interests}
        userInterest={userInterests}
        show={showInterestsGate}
        onHide={() => setShowInterestsGate(false)}
        blocking
        onSubmitSuccess={handleInterestsSubmitSuccess}
      />
      <SignInModal
        open={showSignInModal}
        hideModal={() => setShowSignInModal(false)}
      />
    </>
  ) : (
    goSomeWhere()
  );
}

export default HomePage;
